import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnalyticsService } from 'shared/src/services/analytics.service';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  of,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';
import { AuthService } from 'application/src/app/services/auth.service';
import {
  LibraryItem,
  LibraryService,
  TableOfContentsEntry,
} from 'application/src/app/services/library.service';
import { PausedItemService } from 'application/src/app/services/paused-item.service';
import { PeechUserService } from 'application/src/app/services/peech-user.service';
import { AudioSink } from './audio-sink';
import { PlayQueueService } from './play-queue.service';
import { PartStream, PartStreamHeaders } from './part-stream';
import { SpeechMarkup } from './speech-markup';
import { StreamError, StreamErrorCode } from './stream-error';

export type PlaybackStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'ended' | 'error';

export interface PlayingItem {
  id: string;
  title: string | null;
  logoUrl: string | null;
  partsCount: number;
  libraryState: string;
  // A non-empty TOC supersedes the parts menu in the panel (iOS IOS-1982).
  tableOfContents: TableOfContentsEntry[];
}

export interface PlayerSnapshot {
  status: PlaybackStatus;
  item: PlayingItem | null;
  partIndex: number;
  partsCount: number;
  currentTime: number;
  // null while nothing is known yet; grows during download when estimated.
  duration: number | null;
  durationIsEstimate: boolean;
  speed: number;
  presetId: string | null;
  error: StreamErrorCode | null;
  // The part ended short of what the server promised — offer a retry, don't advance.
  truncated: boolean;
  // Replay the item from part 1 when it finishes (the iOS repeat-item toggle).
  repeat: boolean;
  // Sleep timer countdown; null when off. Playback pauses when it reaches zero.
  sleepRemainingSeconds: number | null;
  // The idle-listening timeout paused playback (iOS "Still there?"); the panel shows an
  // inline notice with a resume action until the user resumes or dismisses it.
  idlePaused: boolean;
}

const IDLE_SNAPSHOT: PlayerSnapshot = {
  status: 'idle',
  item: null,
  partIndex: 0,
  partsCount: 1,
  currentTime: 0,
  duration: null,
  durationIsEstimate: true,
  speed: 1,
  presetId: null,
  error: null,
  truncated: false,
  repeat: false,
  sleepRemainingSeconds: null,
  idlePaused: false,
};

const SPEED_STORAGE_KEY = 'peech-playing-speed';
export const MIN_SPEED = 0.5;
export const MAX_SPEED = 3;
// iOS PlayerSkipInterval: back 15 s, forward 30 s — the Media Session handlers share them.
export const SKIP_BACK_SECONDS = 15;
export const SKIP_FORWARD_SECONDS = 30;

// Rapid next/next/next taps must coalesce into one stream request (each request may
// trigger synthesis and a credit charge upstream) — the iOS debounce, same value.
const PART_SWITCH_DEBOUNCE_MS = 600;

// Listening only counts as "in progress" after a real dwell, so a stray tap doesn't
// reorder the library (iOS listeningDwellSeconds).
const PROGRESS_DWELL_MS = 10_000;

// Same bound the HTTP interceptor puts on its 401 refresh.
const TOKEN_REFRESH_TIMEOUT_MS = 10_000;

// Resume rewind (WEB-154). iOS has no rewind constant to mirror (it restores pausedItem at
// the exact saved second), so these are web-chosen: a cold resume — the first play of a
// users/{uuid}.pausedItem restored on app open — goes back 10 s to rebuild context, and an
// in-session resume after a pause of 2+ minutes goes back 3 s. Both clamp at 0:00 of the
// CURRENT part; a rewind never crosses into the previous part.
const COLD_RESUME_REWIND_SECONDS = 10;
const BREAK_RESUME_REWIND_SECONDS = 3;
const BREAK_RESUME_THRESHOLD_MS = 2 * 60_000;
// The rewound position is written to pausedItem only after playback has audibly moved on
// from where the rewind landed — otherwise a resume→pause cycle that never played would
// walk the stored position backwards by 10 s per app open.
const REWIND_SETTLE_SECONDS = 0.5;

// iOS PlaylistManager idle listening: every fire stretches the next timeout by this factor
// and the count is never reset within a session (it does reset on sign-out / account switch).
const IDLE_TIMEOUT_GROWTH = 1.2;

@Injectable({ providedIn: 'root' })
export class PlayerService {
  private readonly auth = inject(AuthService);
  private readonly library = inject(LibraryService);
  private readonly peechUser = inject(PeechUserService);
  private readonly pausedItemStore = inject(PausedItemService);
  private readonly playQueue = inject(PlayQueueService);
  private readonly analytics = inject(AnalyticsService);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly audio = new Audio();
  private readonly sink: AudioSink;

  private stream: PartStream | null = null;
  private partMarkups: SpeechMarkup[] = [];
  private partHeaders: PartStreamHeaders | null = null;
  private partAudioBytes = 0;
  private downloadComplete = false;
  private partSwitchTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingPartIndex: number | null = null;
  private dwellTimer: ReturnType<typeof setTimeout> | null = null;
  private dwellElapsed = false;
  private playedMsAccumulated = 0;
  private playingSince: number | null = null;
  private sleepTimer: ReturnType<typeof setInterval> | null = null;
  private lastTimeEmitMs = 0;
  // A pause during 'loading' has nothing to pause yet — remember the intent so the sink's
  // ready callback doesn't autoplay over it (covers the sleep timer firing mid-load too).
  private userPaused = false;

  // Cross-device resume (users/{uuid}.pausedItem). pendingRestoreSeek is set while the
  // panel shows a restored item that has never streamed: the first resume() consumes it,
  // starts the part and hands the offset to resumeSeekTarget, which is applied once the
  // buffer reaches it. Both die on any teardown — an explicit item/part choice wins.
  private pendingRestoreSeek: number | null = null;
  private resumeSeekTarget: number | null = null;
  // iOS isRestoringPausedItem: applying the restored-paused state must not itself write
  // pausedItem back (a write loop against other devices).
  private restoringPausedItem = false;

  // The identity signed in right now; restorePausedItem re-checks it after every await.
  private activeUserId: string | null = null;

  // failWith is idempotent per stream generation: the generation that already failed.
  private failedGeneration: number | null = null;

  // When the current pause began — drives the in-session break rewind on resume.
  private pausedAtMs: number | null = null;
  // While a rewind is pending or freshly landed, writePausedItem reports THIS position (the
  // stored offset on a cold resume, the pre-rewind position on a break resume) instead of
  // the audio clock; released once playback passes anchorReleaseTime.
  private pausedItemAnchor: number | null = null;
  private anchorReleaseTime = 0;

  // Idle listening (iOS PlaylistManager idle extension): users/{uuid}.idleListeningTimeout
  // in seconds, null = disabled. The 1 Hz check compares Date.now() against the last
  // player interaction, so a suspended tab still fires on the next tick after it wakes.
  private idleTimeoutSeconds: number | null = null;
  private idleTimer: ReturnType<typeof setInterval> | null = null;
  private lastInteractionMs = Date.now();
  private idleFireCount = 0;

  // users/{uuid}.isAutoPlayEnabled mirrored from the shared profile listener; iOS default
  // is ON when the field is absent — but null (profile not yet decoded / signed out) must
  // NOT count as on: an item ending in that loading window would auto-advance over a
  // persisted opt-out. Unknown means no auto-advance.
  private isAutoPlayEnabled: boolean | null = null;

  // Items already played in the current autoplay chain (iOS autoplayPlayedItemIds) — the
  // wrap-around search must not replay them. Reset whenever the USER picks an item.
  private autoplayPlayedItemIds = new Set<string>();

  // Generation guard: every part/item/preset change bumps it, and late async callbacks
  // (stream chunks, token refreshes) from a previous generation are dropped.
  private generation = 0;

  private readonly snapshotSubject = new BehaviorSubject<PlayerSnapshot>({
    ...IDLE_SNAPSHOT,
    speed: restoreSpeed(),
  });
  readonly snapshot$: Observable<PlayerSnapshot> = this.snapshotSubject.asObservable();

  // Cumulative word timings for the CURRENT part only; time/start are part-relative.
  private readonly markupsSubject = new BehaviorSubject<SpeechMarkup[]>([]);
  readonly markups$: Observable<SpeechMarkup[]> = this.markupsSubject.asObservable();

  // Fires after a progress write lands, so pages holding one-shot Firestore reads (the
  // library) can update their rows without a refetch — including the part index, which
  // resumePartIndex() reads off cached row items.
  private readonly progressPersistedSubject = new Subject<{
    itemId: string;
    partIndex: number;
    completed: boolean;
  }>();
  readonly progressPersisted$ = this.progressPersistedSubject.asObservable();

  constructor() {
    this.sink = new AudioSink(this.audio, message => this.onSinkError(message));
    this.audio.preload = 'auto';
    if ('preservesPitch' in this.audio) {
      this.audio.preservesPitch = true;
    }
    this.bindAudioEvents();

    // A sign-out or account switch must kill playback: the audio already buffered would
    // happily keep playing another account's content. The autoplay chain and queue
    // context die with it — the next session (often a restored paused item, which never
    // passes through playItem's consume) must not inherit this account's Completed/tag
    // scope or played ids.
    let lastUid: string | null = null;
    this.auth.user$
      .pipe(
        tap(user => {
          const uid = user?.uid ?? null;

          if (uid !== lastUid) {
            if (this.snapshot.status !== 'idle') {
              this.stop();
            }

            this.autoplayPlayedItemIds = new Set();
            this.playQueue.resetContext();
            // The idle back-off is per listening session; the next account must not inherit
            // this one's stretched timeout or stale interaction clock.
            this.idleFireCount = 0;
            this.markInteraction();
          }

          lastUid = uid;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // Mirror of iOS handleAppTermination: a tab closing mid-playback drops a best-effort
    // resume point. Fire-and-forget — the page may die before the write flushes, and
    // that is acceptable.
    // 'loading' counts too: a tab closed during the part-switch debounce or the stream
    // load would otherwise leave the previous marker (or none) instead of the part the
    // user just chose — writePausedItem already records a pending switch at its 0:00.
    window.addEventListener('pagehide', () => {
      if (this.snapshot.status === 'playing' || this.snapshot.status === 'loading') {
        this.writePausedItem();
      }
    });

    // The autoplay preference, live: flipping it in Account mid-listen affects the very
    // item that is currently playing. The users/{uuid} listener is shared and already
    // held open by the header, so this adds no Firestore cost.
    this.peechUser.profile$
      .pipe(
        tap(profile => {
          // mapProfile already applies the iOS absent-field default (true); a null profile
          // stays null here so the loading window can't override a persisted opt-out.
          this.isAutoPlayEnabled = profile?.isAutoPlayEnabled ?? null;

          // The idle timeout is live too: the backend may enable it mid-session.
          const previousTimeout = this.idleTimeoutSeconds;
          this.idleTimeoutSeconds = profile?.idleListeningTimeout ?? null;

          if (this.idleTimeoutSeconds === null) {
            this.cancelIdleTracking();
          } else if (this.snapshot.status === 'playing') {
            // A timeout that just turned on (null → value) mid-session must not immediately
            // fire against interactions from before it existed — start its clock now, giving
            // the listener the full grace period, same as a fresh 'playing' transition.
            if (previousTimeout === null) {
              this.markInteraction();
            }
            this.startIdleTracking();
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // iOS applicationDidBecomeActive: coming back to the tab counts as presence.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.lastInteractionMs = Date.now();
      }
    });

    // Cross-device resume (iOS checkAudioToPlay): once the Peech identity is known and
    // the player is idle, surface users/{uuid}.pausedItem as a paused panel. Keyed to the
    // userId so it re-runs per account switch, not per identity re-emit.
    this.peechUser.details$
      .pipe(
        distinctUntilChanged((previous, current) => previous?.userId === current?.userId),
        tap(details => {
          // Tracked so an in-flight restore can notice a sign-out/account switch between
          // its awaits — the idle check alone would let a cached read publish the previous
          // account's item into the new session.
          this.activeUserId = details?.userId ?? null;

          if (details && this.snapshot.status === 'idle') {
            void this.restorePausedItem(details.userId);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  get snapshot(): PlayerSnapshot {
    return this.snapshotSubject.value;
  }

  // Direct time read for the rAF-driven highlighter — no observable overhead at 60Hz.
  get currentTime(): number {
    return this.audio.currentTime;
  }

  get markups(): SpeechMarkup[] {
    return this.markupsSubject.value;
  }

  isCurrent(itemId: string): boolean {
    return this.snapshot.item?.id === itemId && this.snapshot.status !== 'idle';
  }

  // Play an item from its resume position, or toggle when it is already the current one.
  toggle(item: LibraryItem): void {
    if (this.isCurrent(item.id)) {
      if (this.snapshot.status === 'ended') {
        // A replay is a new chain — it adopts the context announced for this toggle
        // (consumed inside replayEndedItem), e.g. the Completed/tag row it started from.
        this.replayEndedItem();
        return;
      }

      // Pause/resume/retry continue the SAME chain — a context the library just announced
      // must not linger for a later, unrelated start.
      this.playQueue.discardPendingContext();

      // 'loading' pauses too: pause() records the intent so the pending download won't
      // autoplay over it.
      if (this.snapshot.status === 'playing' || this.snapshot.status === 'loading') {
        this.pause();
      } else if (this.snapshot.status === 'error') {
        this.playPart(this.snapshot.partIndex, 'retry');
      } else {
        this.resume();
      }
      return;
    }

    this.playItem(item, resumePartIndex(item));
  }

  playItem(item: LibraryItem, partIndex: number, presetId?: string | null): void {
    // A user-picked item starts a fresh autoplay chain and adopts (or defaults) the
    // queue context announced for this start.
    this.markInteraction();
    this.playQueue.consumePendingContext();
    this.autoplayPlayedItemIds = new Set([item.id.toLowerCase()]);
    this.startItem(item, partIndex, presetId);
  }

  // Explicitly replaying a finished item is a new listening chain — playPart's ended
  // check does the actual chain/context reset, this alias just keeps call sites
  // expressive. Public — the audio panel's play button replays too.
  replayEndedItem(): void {
    this.playPart(0, 'replay');
  }

  // Restarting playback of a finished item — replay button or a reader part pick — opens
  // a new chain: the previous run's played ids are forgotten (or the replayed item could
  // never advance anywhere), and the context announced for this start is adopted (a
  // library-row replay) or reset to the default (panel, MediaSession, reader — nothing
  // announced).
  private beginNewChain(): void {
    const itemId = this.snapshot.item?.id;

    this.playQueue.consumePendingContext();

    if (itemId) {
      this.autoplayPlayedItemIds = new Set([itemId.toLowerCase()]);
    }
  }

  private startItem(item: LibraryItem, partIndex: number, presetId?: string | null): void {
    const partsCount = Math.max(1, item.partsCount ?? 1);

    this.teardownPart();
    this.update({
      ...IDLE_SNAPSHOT,
      status: 'loading',
      item: {
        id: item.id,
        title: item.title,
        logoUrl: item.logoUrl,
        partsCount,
        libraryState: item.libraryState,
        tableOfContents: item.tableOfContents ?? [],
      },
      partsCount,
      partIndex: clampPart(partIndex, partsCount),
      presetId: presetId !== undefined ? presetId : (item.currentVoicePresetId ?? null),
      speed: this.snapshot.speed,
      // The sleep timer is session-scoped, not item-scoped — it keeps ticking across
      // item changes (repeat, by contrast, resets: it belongs to the finished item).
      sleepRemainingSeconds: this.snapshot.sleepRemainingSeconds,
    });

    // markInteraction lives in the USER-INITIATED callers (playItem), not here: iOS
    // PlaylistManager.playItem only posts PLAYER_USER_INTERACTION_DETECTED when the call
    // resets the autoplay history, and explicitly keeps auto-advance silent so the
    // continuous-listening idle pause (IOS-1931) can still trip across an autoplay chain.
    this.analytics.track('playback_resumed', {
      trigger: 'item_start',
      item_id: item.id,
      part_number: this.snapshot.partIndex + 1,
    });
    this.beginDwellTracking();
    this.loadCurrentPart();
  }

  resume(): void {
    if (this.snapshot.status !== 'paused') {
      return;
    }

    this.markInteraction();
    const wasIdlePaused = this.snapshot.idlePaused;

    // First resume of a restored pausedItem: no stream exists yet (restore deliberately
    // never starts one — autoplay policy would block it anyway), so start the part now
    // and seek to the (rewound) offset once the buffer reaches it.
    if (this.pendingRestoreSeek !== null) {
      const stored = this.pendingRestoreSeek;
      const target = Math.max(0, stored - COLD_RESUME_REWIND_SECONDS);

      this.teardownPart(); // resets pendingRestoreSeek/resumeSeekTarget/anchor too
      this.resumeSeekTarget = target > 0 ? target : null;
      this.holdPausedItemAnchor(stored, target);
      this.update({ status: 'loading', idlePaused: false });
      this.analytics.track('playback_resumed', {
        trigger: 'restored_item',
        rewind_seconds: stored - target,
      });
      this.beginDwellTracking();
      this.loadCurrentPart();
      return;
    }

    const rewound = this.applyBreakRewind();

    this.userPaused = false;
    void this.audio.play().catch(() => undefined);
    this.update({ idlePaused: false });
    this.analytics.track('playback_resumed', { trigger: 'unpause', rewind_seconds: rewound });

    if (wasIdlePaused) {
      this.analytics.track('idle_listening_resumed');
    }
  }

  pause(): void {
    this.markInteraction();

    if (this.pausePlayback()) {
      this.analytics.track('playback_paused');
    }
  }

  // Shared by the user pause, the sleep timer and the idle timeout — the status flip and
  // the pausedItem write are identical (iOS AccountHandler saves on every transition to
  // .paused, whichever path caused it); only the analytics differ per caller.
  private pausePlayback(): boolean {
    if (this.snapshot.status !== 'playing' && this.snapshot.status !== 'loading') {
      return false;
    }

    this.userPaused = true;
    this.pausedAtMs = Date.now();
    this.audio.pause();

    if (this.snapshot.status === 'loading') {
      // Nothing is playing yet; flip the status explicitly so the panel reflects the
      // intent instead of spinning until the download lands.
      this.update({ status: 'paused' });
    }

    // iOS AccountHandler: every user-visible pause drops the cross-device resume point.
    this.writePausedItem();
    return true;
  }

  // iOS "Still there?" secondary button: acknowledge the auto-pause and stay paused.
  dismissIdleNotice(): void {
    this.markInteraction();
    this.update({ idlePaused: false });
  }

  stop(): void {
    // Closing the player abandons the resume point (iOS: player .stopped →
    // removePausedItem). Natural part transitions never come through here.
    const hadItem = this.snapshot.item !== null;

    this.teardownPart();
    this.onPlaybackStopped();
    this.clearSleepTimer();
    this.clearMediaSession();
    this.update({ ...IDLE_SNAPSHOT, speed: this.snapshot.speed });
    this.pausedAtMs = null;

    if (hadItem) {
      this.clearPausedItem();
    }
  }

  seek(seconds: number): void {
    this.markInteraction();

    // An explicit seek overrides a still-unapplied restore offset, the position it was
    // holding for pausedItem, and any pending break rewind: the user has chosen where
    // they are, so a later resume must not rewind from a position they never played to.
    this.resumeSeekTarget = null;
    this.pausedItemAnchor = null;
    this.pausedAtMs = null;

    const ceiling = this.seekCeiling();
    this.audio.currentTime = Math.min(Math.max(0, seconds), ceiling);
    this.update({ currentTime: this.audio.currentTime });

    // A seek while paused moves the resume point too — pagehide only writes while
    // playing, so without this a reload restores the pre-seek position. Not in the
    // restored-never-streamed state: no source exists yet and the ceiling clamps to 0,
    // which would wipe the stored offset.
    if (this.snapshot.status === 'paused' && this.pendingRestoreSeek === null) {
      this.writePausedItem();
    }
  }

  skipForward(): void {
    this.markInteraction();
    const target = this.audio.currentTime + SKIP_FORWARD_SECONDS;
    const pastEnd =
      this.downloadComplete && this.snapshot.duration !== null && target >= this.snapshot.duration;

    // On the last part there is no next — clamp-seek toward the end instead of
    // swallowing the tap.
    if (pastEnd && this.snapshot.partIndex < this.snapshot.partsCount - 1) {
      this.nextPart();
    } else {
      this.seek(target);
    }

    this.analytics.track('playback_skip_forward', { new_time_sec: target });
  }

  skipBackward(): void {
    // Explicit here as well: the cross-part branch below bypasses seek()'s own mark.
    this.markInteraction();
    const target = this.audio.currentTime - SKIP_BACK_SECONDS;

    if (target < 0 && this.snapshot.partIndex > 0) {
      this.schedulePart(this.snapshot.partIndex - 1);
    } else {
      this.seek(Math.max(0, target));
    }

    this.analytics.track('playback_skip_backward', { new_time_sec: Math.max(0, target) });
  }

  nextPart(): void {
    this.markInteraction();

    if (this.snapshot.partIndex < this.snapshot.partsCount - 1) {
      this.schedulePart(this.snapshot.partIndex + 1);
    }
  }

  previousPart(): void {
    this.markInteraction();

    if (this.snapshot.partIndex > 0) {
      this.schedulePart(this.snapshot.partIndex - 1);
    }
  }

  playPart(
    partIndex: number,
    source: 'part_pill' | 'parts_menu' | 'retry' | 'replay' = 'parts_menu'
  ): void {
    if (!this.snapshot.item) {
      return;
    }

    this.markInteraction();

    // A part pick on an ENDED item restarts listening — reader part pills and the parts
    // menu reach here directly, so the new-chain reset must live on this path, not only
    // on the replay button.
    if (this.snapshot.status === 'ended') {
      this.beginNewChain();
    }

    // Retry and replay reuse this path but are not part picks; only the part pill and the
    // parts menu count toward the shared part-switch metric.
    if (source === 'part_pill' || source === 'parts_menu') {
      this.analytics.track('user_switched_playing_part', {
        new_part_number: partIndex + 1,
        source,
      });
    }
    // Through the same debounce as next/prev: part pills and the parts menu can be
    // tapped just as rapidly, and every stream request may charge synthesis upstream.
    this.schedulePart(clampPart(partIndex, this.snapshot.partsCount));
  }

  setSpeed(speed: number): void {
    this.markInteraction();
    const clamped = Math.min(MAX_SPEED, Math.max(MIN_SPEED, speed));
    this.audio.playbackRate = clamped;
    localStorage.setItem(SPEED_STORAGE_KEY, String(clamped));
    this.update({ speed: clamped });
    this.analytics.track('playback_speed_changed', { new_speed: clamped, source: 'audio_panel' });
  }

  // Restarts the current part from 0:00 with the new voice (iOS semantics). The server
  // persists a non-default voicePresetId as the user/item pick — no Firestore write here.
  setPreset(presetId: string, title?: string): void {
    if (!this.snapshot.item || presetId === this.snapshot.presetId) {
      return;
    }

    this.markInteraction();
    this.update({ presetId });
    this.analytics.track('voice_preset_selected', {
      preset_id: presetId,
      title,
      source: 'audio_panel',
    });
    this.startPart(this.snapshot.partIndex);
  }

  // An explicit retry is presence too (iOS posts the interaction notification from play):
  // without it the playback-start hook resumes idle tracking against a stale clock and the
  // retried stream auto-pauses on the next 1 Hz check.
  retryPart(): void {
    this.markInteraction();
    this.startPart(this.snapshot.partIndex);
  }

  toggleRepeat(): void {
    this.markInteraction();
    const repeat = !this.snapshot.repeat;
    this.update({ repeat });
    this.analytics.track('play_item_on_repeat_toggled', { turned_on: repeat });
  }

  // null switches the timer off. Firing pauses (not stops) — the listener wakes up next
  // to their place in the item, not to a closed player.
  setSleepTimer(minutes: number | null): void {
    this.markInteraction();
    this.clearSleepTimer();

    if (minutes === null) {
      this.update({ sleepRemainingSeconds: null });

      // iOS: clearing the sleep timer hands the session back to idle tracking, with a
      // fresh clock (markInteraction above) so a long sleep timer can't produce an
      // immediate idle pause.
      if (this.snapshot.status === 'playing') {
        this.startIdleTracking();
      }
      return;
    }

    // iOS: an active sleep timer bypasses idle tracking — the listener already asked to
    // be stopped at a known time.
    this.cancelIdleTracking();

    const deadline = Date.now() + minutes * 60_000;
    this.update({ sleepRemainingSeconds: minutes * 60 });
    this.analytics.track('sleep_timer_set', { minutes });

    this.sleepTimer = setInterval(() => {
      const remaining = Math.ceil((deadline - Date.now()) / 1000);

      if (remaining <= 0) {
        this.clearSleepTimer();
        this.update({ sleepRemainingSeconds: null });

        // The timer can fire while an auto-advance lookup is in flight (status 'ended',
        // pause() a no-op) — bump the generation so that lookup's result is dropped and
        // the "supposedly stopped" session can't start a fresh item. ONLY at 'ended':
        // in any live state the bump would instead orphan the active PartStream's
        // callbacks and strand the buffered audio.
        if (this.snapshot.status === 'ended') {
          this.generation += 1;
        }

        this.pause();
        this.analytics.track('sleep_timer_fired');
        return;
      }

      this.update({ sleepRemainingSeconds: remaining });
    }, 1000);
  }

  private clearSleepTimer(): void {
    if (this.sleepTimer !== null) {
      clearInterval(this.sleepTimer);
      this.sleepTimer = null;
    }
  }

  // ── Part lifecycle ──────────────────────────────────────────────────────────

  private schedulePart(partIndex: number): void {
    this.pendingPartIndex = partIndex;
    // Show the target part immediately; the stream request follows after the debounce.
    // The OLD part's stream must die right here, not at debounce end — a late markup or
    // done/error callback from it would be attributed to the new part (and corrupt the
    // reader's locator cursor, killing the karaoke highlight for the whole part).
    this.abortStream();
    this.audio.pause();
    this.update({
      status: 'loading',
      partIndex,
      currentTime: 0,
      duration: null,
      idlePaused: false,
    });

    if (this.partSwitchTimer) {
      clearTimeout(this.partSwitchTimer);
    }

    this.partSwitchTimer = setTimeout(() => {
      this.partSwitchTimer = null;

      if (this.pendingPartIndex !== null) {
        const target = this.pendingPartIndex;
        this.pendingPartIndex = null;
        this.startPart(target);
      }
    }, PART_SWITCH_DEBOUNCE_MS);
  }

  private startPart(partIndex: number): void {
    this.teardownPart();
    this.update({
      status: 'loading',
      partIndex,
      currentTime: 0,
      duration: null,
      durationIsEstimate: true,
      error: null,
      truncated: false,
      idlePaused: false,
    });
    // A manual part jump is listening progress too, not just natural part endings.
    // The dwell guard inside keeps stray taps from writing.
    this.persistProgress(partIndex, false);
    this.loadCurrentPart();
  }

  private loadCurrentPart(): void {
    const item = this.snapshot.item;

    if (!item) {
      return;
    }

    const generation = ++this.generation;

    this.markupsSubject.next([]);
    this.partMarkups = [];
    this.partHeaders = null;
    this.partAudioBytes = 0;
    this.downloadComplete = false;

    this.userPaused = false;
    this.sink.beginPart(() => {
      if (generation !== this.generation) {
        return;
      }

      if (this.userPaused) {
        this.zone.run(() => this.update({ status: 'paused' }));
        return;
      }

      void this.audio.play().catch(() => {
        // Autoplay refusals (browser policy) land here — surface a resumable state
        // instead of an eternal spinner.
        if (generation === this.generation) {
          this.zone.run(() => this.update({ status: 'paused' }));
        }
      });
    });

    void this.fetchPart(generation, item.id, this.snapshot.partIndex, false);
  }

  private async fetchPart(
    generation: number,
    itemId: string,
    partIndex: number,
    isAuthRetry: boolean
  ): Promise<void> {
    // Session must be settled before reading token$ — same ordering contract the HTTP
    // interceptor relies on.
    await firstValueFrom(this.auth.authInitialized$.pipe(filter(initialized => initialized)));
    const token = await firstValueFrom(this.auth.token$.pipe(take(1)));

    if (generation !== this.generation) {
      return;
    }

    if (!token) {
      this.zone.run(() => this.failWith('UNAUTHORIZED'));
      return;
    }

    this.stream = new PartStream(
      { itemId, partIndex, voicePresetId: this.snapshot.presetId, token },
      {
        onHeaders: headers => this.ifCurrent(generation, () => this.onHeaders(headers)),
        onAudio: chunk => {
          if (generation === this.generation) {
            this.partAudioBytes += chunk.byteLength;
            this.sink.append(chunk);
          }
        },
        onMarkup: markups => this.ifCurrent(generation, () => this.onMarkups(markups)),
        onDone: result =>
          this.ifCurrent(generation, () => this.onStreamDone(result.truncated, result.audioBytes)),
        onError: error => {
          if (generation !== this.generation) {
            return;
          }

          // The stream bypasses the HTTP interceptor, so its 401 retry lives here: one
          // forced token refresh, bounded like the interceptor's — a refresh that never
          // settles must not strand the player in 'loading' forever.
          if (error.code === 'UNAUTHORIZED' && !isAuthRetry) {
            this.auth
              .updateToken()
              .pipe(
                timeout({ each: TOKEN_REFRESH_TIMEOUT_MS, with: () => of(null) }),
                tap(refreshedToken => {
                  if (generation !== this.generation) {
                    return;
                  }

                  if (refreshedToken) {
                    void this.fetchPart(generation, itemId, partIndex, true);
                  } else {
                    this.zone.run(() => this.failWith('UNAUTHORIZED', 'Token refresh failed'));
                  }
                })
              )
              .subscribe();
            return;
          }

          this.zone.run(() => this.failWith(error.code, error.message));
        },
      }
    );
    this.stream.start();
  }

  private onHeaders(headers: PartStreamHeaders): void {
    this.partHeaders = headers;

    if (headers.voicePresetId) {
      // The server may substitute a deleted/ineligible preset; adopt the real one.
      this.zone.run(() => this.update({ presetId: headers.voicePresetId }));
    }
  }

  private onMarkups(markups: SpeechMarkup[]): void {
    this.partMarkups = this.partMarkups.concat(markups);
    this.zone.run(() => this.markupsSubject.next(this.partMarkups));
  }

  private onStreamDone(truncated: boolean, audioBytes: number): void {
    this.downloadComplete = true;
    this.sink.end();

    if (truncated) {
      this.zone.run(() => this.update({ truncated: true }));
      this.analytics.track('playback_part_truncated', {
        part_number: this.snapshot.partIndex + 1,
        bytes_downloaded: audioBytes,
        bytes_expected: this.partHeaders?.audioContentLength ?? undefined,
      });
    }
  }

  // Kills the in-flight stream and everything derived from it, so no late callback can
  // pass the generation guard and be attributed to whatever plays next.
  private abortStream(): void {
    this.generation++;
    this.stream?.stop();
    this.stream = null;
    this.partMarkups = [];
    this.markupsSubject.next([]);
  }

  private teardownPart(): void {
    this.abortStream();

    if (this.partSwitchTimer) {
      clearTimeout(this.partSwitchTimer);
      this.partSwitchTimer = null;
    }
    this.pendingPartIndex = null;
    this.pendingRestoreSeek = null;
    this.resumeSeekTarget = null;
    this.pausedItemAnchor = null;
    this.cancelIdleTracking();

    this.audio.pause();
    this.sink.dispose();
  }

  // Idempotent per stream generation: the first failure wins, later reports for the same
  // generation (a dead SourceBuffer re-throwing per chunk, the element's own 'error' event
  // for the cause the sink already reported) change nothing and track nothing. A retry
  // starts a new generation through startPart(), so it can fail — and be tracked — again.
  private failWith(code: StreamErrorCode, message?: string): void {
    if (this.failedGeneration === this.generation) {
      return;
    }

    this.failedGeneration = this.generation;

    // Nothing downstream can consume more bytes; stop paying for the download. Not a
    // generation bump — retryPart() does its own teardown.
    this.stream?.stop();
    this.stream = null;

    this.audio.pause();
    this.update({ status: 'error', error: code });
    this.analytics.track('playback_failed', {
      error_code: code,
      message,
      part_number: this.snapshot.partIndex + 1,
      item_id: this.snapshot.item?.id,
      attempt: 1,
      will_retry: false,
    });
  }

  private onSinkError(message: string): void {
    this.zone.run(() => this.failWith('UNKNOWN', message));
  }

  private ifCurrent(generation: number, action: () => void): void {
    if (generation === this.generation) {
      action();
    }
  }

  // ── Audio element events ────────────────────────────────────────────────────

  private bindAudioEvents(): void {
    this.audio.addEventListener('play', () => {
      this.audio.playbackRate = this.snapshot.speed;
      this.zone.run(() => this.update({ status: 'playing', idlePaused: false }));
      this.syncMediaSession();
    });

    // The dwell counts audibly-playing time: 'play' fires on the play() CALL, before any
    // byte is buffered — 'playing' fires when sound actually starts, 'waiting' when a
    // stall suspends it.
    this.audio.addEventListener('playing', () => this.onPlaybackStarted());
    this.audio.addEventListener('waiting', () => this.onPlaybackStopped());

    this.audio.addEventListener('pause', () => {
      this.onPlaybackStopped();
      // iOS: .paused/.stopped cancel idle tracking; .buffering ('waiting' here) does not.
      this.cancelIdleTracking();

      // 'pause' also fires as part of 'ended' and teardown; only a real user-visible
      // pause of the current part should flip the status.
      if (this.snapshot.status === 'playing' && !this.audio.ended) {
        this.pausedAtMs = Date.now();
        this.zone.run(() => this.update({ status: 'paused' }));
      }
    });

    this.audio.addEventListener('timeupdate', () => {
      // Cheap check before the throttle: the restore seek must land as soon as the
      // buffer allows, not on the next 500ms tick.
      this.applyResumeSeekIfReady();
      this.releasePausedItemAnchorIfSettled();

      // Native timeupdate fires ~4Hz and each snapshot emit drains whole-app change
      // detection (default CD) — 2Hz is plenty for second-granular time labels.
      const now = Date.now();

      if (now - this.lastTimeEmitMs < 500) {
        return;
      }

      this.lastTimeEmitMs = now;
      this.zone.run(() =>
        this.update({ currentTime: this.audio.currentTime, duration: this.knownDuration() })
      );
    });

    this.audio.addEventListener('durationchange', () => {
      // endOfStream() lands here with the final duration — the last chance to resolve a
      // restore target that sits beyond the part's real end.
      this.applyResumeSeekIfReady();
      this.zone.run(() => this.update({ duration: this.knownDuration() }));
    });

    this.audio.addEventListener('ended', () => {
      this.zone.run(() => this.onPartEnded());
    });

    this.audio.addEventListener('error', () => {
      // Decode/element failures otherwise strand the panel in a playing/loading state.
      if (this.snapshot.status === 'idle' || this.snapshot.status === 'error') {
        return;
      }

      // The element is dead for this part: every further appendBuffer would throw
      // InvalidStateError against its error attribute. Silence the sink before the next
      // chunk arrives so this event is the single report of the failure.
      this.sink.halt();
      this.zone.run(() => this.failWith('UNKNOWN', this.audio.error?.message));
    });
  }

  private onPartEnded(): void {
    // A debounced part switch is already in flight; this 'ended' belongs to the part the
    // user just navigated away from — advancing here would double-switch.
    if (this.partSwitchTimer !== null || this.pendingPartIndex !== null) {
      return;
    }

    if (this.snapshot.truncated) {
      // Hold the position and offer a retry — advancing would silently skip content.
      this.failWith('TRUNCATED', 'Stream delivered less audio than promised');
      return;
    }

    if (this.snapshot.partIndex < this.snapshot.partsCount - 1) {
      this.persistProgress(this.snapshot.partIndex + 1, false);
      this.startPart(this.snapshot.partIndex + 1);
      return;
    }

    this.persistProgress(this.snapshot.partIndex, true);
    this.analytics.track('playback_stop', { reason: 'completed', item_id: this.snapshot.item?.id });

    if (this.snapshot.repeat) {
      this.startPart(0);
      return;
    }

    this.update({ status: 'ended' });
    // A completed listen abandons the resume point too — otherwise an earlier pause's
    // marker would resurrect the finished item at a stale offset on the next open.
    this.clearPausedItem();

    // iOS PlaylistManager.onPlayerFinishedPlayingLibraryItem: with autoplay on, continue
    // with the next eligible library item. 'ended' is already showing, so a slow (or
    // empty) lookup degrades to exactly the old behavior.
    if (this.isAutoPlayEnabled) {
      this.autoAdvance();
    }
  }

  private autoAdvance(): void {
    const finishedId = this.snapshot.item?.id;

    if (!finishedId) {
      return;
    }

    this.autoplayPlayedItemIds.add(finishedId.toLowerCase());

    // Observed, not bumped: any user action meanwhile (new item, part pick, stop) bumps
    // the generation through its own teardown and this lookup's result must then be
    // dropped. The status re-check covers a pause of the ended panel too.
    const generation = this.generation;

    this.peechUser.details$
      .pipe(
        take(1),
        filter(details => details !== null),
        switchMap(details =>
          this.playQueue.findNext(details.userId, finishedId, this.autoplayPlayedItemIds).pipe(
            catchError(() => of(null)),
            tap(next => {
              // Re-checked at delivery time, not just at kickoff: while the lookup paged
              // through the library the user may have turned autoplay off, or the signed-in
              // identity may have changed in another tab (the lookup ran under the OLD
              // account — starting its item would leak it into the new session).
              if (
                !next ||
                generation !== this.generation ||
                this.snapshot.status !== 'ended' ||
                !this.isAutoPlayEnabled ||
                details.userId !== this.activeUserId
              ) {
                return;
              }

              this.analytics.track('playback_autoplay_advanced', {
                item_id: next.id,
                finished_item_id: finishedId,
              });
              this.autoplayPlayedItemIds.add(next.id.toLowerCase());
              this.startItem(next, resumePartIndex(next));
            })
          )
        )
      )
      .subscribe();
  }

  // ── Duration ────────────────────────────────────────────────────────────────

  private knownDuration(): number | null {
    if (Number.isFinite(this.audio.duration) && this.audio.duration > 0) {
      this.update({ durationIsEstimate: false });
      return this.audio.duration;
    }

    const bitrate = this.partHeaders?.bitrateKbps ?? 48;
    const bytes = this.partHeaders?.audioContentLength ?? this.partAudioBytes;

    if (!bytes) {
      return null;
    }

    // Grows with the download when the server didn't know the size up front.
    return (bytes * 8) / (bitrate * 1000);
  }

  private seekCeiling(): number {
    const buffered = this.sink.bufferedEnd();

    return buffered > 0 ? Math.max(0, buffered - 0.05) : 0;
  }

  // ── Progress persistence ────────────────────────────────────────────────────

  // The dwell counts PLAYED time only — a slow download or an immediate pause must not
  // mark an untouched item inProgress. Accrued playtime is tracked from the audio
  // element's play/pause events; the timer only runs while playing.
  private beginDwellTracking(): void {
    this.clearDwellTimer();
    this.dwellElapsed = false;
    this.playedMsAccumulated = 0;
    this.playingSince = null;
  }

  private onPlaybackStarted(): void {
    this.startIdleTracking();

    if (this.playingSince === null) {
      this.playingSince = Date.now();
    }

    if (!this.dwellElapsed && this.dwellTimer === null) {
      const remaining = Math.max(0, PROGRESS_DWELL_MS - this.playedMsAccumulated);

      this.dwellTimer = setTimeout(() => {
        this.dwellTimer = null;
        this.dwellElapsed = true;
        this.persistProgress(this.snapshot.partIndex, false);
      }, remaining);
    }
  }

  private onPlaybackStopped(): void {
    if (this.playingSince !== null) {
      this.playedMsAccumulated += Date.now() - this.playingSince;
      this.playingSince = null;
    }

    this.clearDwellTimer();
  }

  private clearDwellTimer(): void {
    if (this.dwellTimer) {
      clearTimeout(this.dwellTimer);
      this.dwellTimer = null;
    }
  }

  // The library moved an item's state by hand (Move to / bulk action). Keep the snapshot's
  // cached copy current so the never-downgrade guard in persistProgress sees the new state
  // instead of writing inProgress over a manual Completed.
  // The library moved this item back to To Start: if it is the loaded item, drop it from the
  // player — stop() also clears users/{uuid}.pausedItem, so neither a resume nor the next app
  // open continues from the old mid-item position.
  resetItemPosition(itemId: string): void {
    if (this.snapshot.item?.id === itemId) {
      this.stop();
    }
  }

  syncLibraryState(itemId: string, libraryState: string): void {
    const item = this.snapshot.item;

    if (item && item.id === itemId && item.libraryState !== libraryState) {
      this.update({ item: { ...item, libraryState } });
    }
  }

  private persistProgress(partIndex: number, completed: boolean): void {
    const item = this.snapshot.item;

    if (!item || (!this.dwellElapsed && !completed)) {
      return;
    }

    // Never downgrade a completed item back to inProgress just because it was replayed.
    if (!completed && item.libraryState === 'completed') {
      return;
    }

    if (completed) {
      // Keep the snapshot's own copy current, so a replay (repeat, or a fresh toggle)
      // hits the guard above instead of downgrading the item it just completed.
      this.update({ item: { ...item, libraryState: 'completed' } });
    }

    // Bind the write to the identity that is signed in NOW — a deferred subscription
    // waiting for details would happily write the old item under whichever account
    // resolves next. If the identity isn't ready, drop the write (the dwell means this
    // is rare, and progress is best-effort).
    this.peechUser.details$
      .pipe(
        take(1),
        filter(details => details !== null),
        switchMap(details =>
          this.library.updateListeningProgress(details.userId, item.id, partIndex, completed).pipe(
            tap(() =>
              this.progressPersistedSubject.next({ itemId: item.id, partIndex, completed })
            ),
            catchError(() => EMPTY)
          )
        )
      )
      .subscribe();
  }

  // ── Cross-device resume (users/{uuid}.pausedItem, iOS AccountHandler) ────────

  // Written on user pause and on pagehide-while-playing; cleared on stop(). Never
  // written while the restored-paused state is being applied (write-loop guard), and
  // bound to the identity signed in NOW — same best-effort rules as persistProgress.
  private writePausedItem(): void {
    const item = this.snapshot.item;

    if (!item || this.restoringPausedItem) {
      return;
    }

    // Mid part-switch debounce the snapshot already points at the target part while the
    // audio still runs the OLD part's clock — pairing them would restore an unrelated
    // position. The switch lands at the target's 0:00, so that is the honest marker.
    const switching = this.partSwitchTimer !== null || this.pendingPartIndex !== null;
    const partIndex = this.pendingPartIndex ?? this.snapshot.partIndex;

    // A pause before a resume rewind has settled must keep the pre-rewind position (the
    // stored offset on a cold resume), not regress it to the rewound one or to the few
    // seconds played from 0:00 while the restore seek waited for the buffer.
    const playedTime = switching ? 0 : (this.pausedItemAnchor ?? this.audio.currentTime);

    // No progress write here: the marker carries its own partIndex, and bypassing the
    // dwell gate would flip a barely-touched item to inProgress on a stray play+pause.

    this.peechUser.details$
      .pipe(
        take(1),
        filter(details => details !== null),
        tap(details => this.pausedItemStore.save(details.userId, item.id, playedTime, partIndex))
      )
      .subscribe();
  }

  private clearPausedItem(): void {
    this.peechUser.details$
      .pipe(
        take(1),
        filter(details => details !== null),
        tap(details => this.pausedItemStore.clear(details.userId))
      )
      .subscribe();
  }

  // Puts the player into a PAUSED panel on the stored item — no stream, no autoplay
  // (browser policy would block it anyway); the first resume() starts the part and
  // seeks. The pausedItem field itself is left in place for other devices.
  private async restorePausedItem(userId: string): Promise<void> {
    const paused = await this.pausedItemStore.read(userId);

    if (!paused || this.snapshot.status !== 'idle' || this.activeUserId !== userId) {
      return;
    }

    // getItemsByIds, not watchItem: the stored id is lowercased (iOS wire format) while
    // item doc ids keep their original casing, and the by-ids fetch already queries both
    // UUID spellings. It also drops deleted docs, matching the isDeleted check below.
    const item = await firstValueFrom(
      this.library.getItemsByIds(userId, [paused.id]).pipe(
        map(items => items[0] ?? null),
        catchError(() => of(null))
      )
    );

    // iOS parity: a missing or deleted item just gives up quietly. The player may also
    // have started something real while the item doc was in flight, or the identity may
    // have flipped (sign-out, account switch) — never clobber, never leak across users.
    if (
      !item ||
      item.isDeleted ||
      this.snapshot.status !== 'idle' ||
      this.activeUserId !== userId
    ) {
      return;
    }

    const partsCount = Math.max(1, item.partsCount ?? 1);

    this.zone.run(() => {
      this.restoringPausedItem = true;
      this.update({
        ...IDLE_SNAPSHOT,
        status: 'paused',
        item: {
          id: item.id,
          title: item.title,
          logoUrl: item.logoUrl,
          partsCount,
          libraryState: item.libraryState,
          tableOfContents: item.tableOfContents ?? [],
        },
        partsCount,
        // The marker's own part wins: listeningPartIndex is monotonic and can point past
        // a pause made on an earlier (replayed) part. Absent (an iOS write) → fall back.
        partIndex: clampPart(paused.partIndex ?? item.listeningPartIndex ?? 0, partsCount),
        presetId: item.currentVoicePresetId ?? null,
        currentTime: paused.playedTime,
        speed: this.snapshot.speed,
        sleepRemainingSeconds: this.snapshot.sleepRemainingSeconds,
      });
      this.restoringPausedItem = false;

      // The restore never passes through playItem, so seed the chain here the same way:
      // a fresh session under the default queue scope, starting from the restored item.
      this.autoplayPlayedItemIds = new Set([item.id.toLowerCase()]);
      this.playQueue.resetContext();

      this.pendingRestoreSeek = paused.playedTime;
      this.analytics.track('playback_position_restored', {
        entry_point: 'cold_launch',
        seconds: Number.isFinite(paused.playedTime) ? Math.trunc(paused.playedTime) : -1,
        item_id: item.id,
        part_number: this.snapshot.partIndex + 1,
      });
    });
  }

  // The demuxed MSE stream cannot seek into unbuffered audio — bytes arrive linearly
  // from 0:00 and there is no Range support server-side — so a restored part starts at
  // the top and jumps to the stored offset the moment the buffer reaches it (or clamps
  // to the buffered end at EOF when the part is shorter than the offset remembers).
  private applyResumeSeekIfReady(): void {
    if (this.resumeSeekTarget === null) {
      return;
    }

    const target = this.resumeSeekTarget;
    const buffered = this.sink.bufferedEnd();

    if (buffered >= target) {
      this.resumeSeekTarget = null;
      this.audio.currentTime = target;
    } else if (this.sink.finalized()) {
      // Blob fallback: 'finalized' means the blob is ASSIGNED, not parsed — until the
      // element reports a seekable/buffered range, clamping would land near 0:00. Keep
      // waiting; the range appears with the element's own loadedmetadata churn.
      const seekable = this.audio.seekable;
      const seekableEnd = seekable.length ? seekable.end(seekable.length - 1) : 0;
      const end = Math.max(buffered, seekableEnd);

      if (end <= 0) {
        return;
      }

      // The part may really be shorter than the stored offset (e.g. re-synthesized with
      // a different voice) — land just before its actual end instead of waiting forever.
      // When the range does cover the target (fully-loaded blob), seek exactly.
      this.resumeSeekTarget = null;
      this.audio.currentTime = Math.min(target, Math.max(0, end - 0.05));
      // A clamped landing sits below the rewind's release point; move the release down
      // with it so the pausedItem anchor can still let go once playback moves on.
      this.anchorReleaseTime = Math.min(
        this.anchorReleaseTime,
        this.audio.currentTime + REWIND_SETTLE_SECONDS
      );
    }
  }

  // ── Resume rewind (WEB-154) ─────────────────────────────────────────────────

  // In-session resume after a 2+ minute break: step back a few seconds to restore
  // context. The audio is already buffered from 0:00 (MSE appends linearly and never
  // evicts; the blob fallback is whole), so the seek is immediate — no buffer wait.
  // Returns the seconds actually rewound (0 when none applied).
  private applyBreakRewind(): number {
    const pausedAt = this.pausedAtMs;
    this.pausedAtMs = null;

    if (pausedAt === null || Date.now() - pausedAt < BREAK_RESUME_THRESHOLD_MS) {
      return 0;
    }

    // A restore seek still waiting for buffer already lands at a rewound spot.
    if (this.resumeSeekTarget !== null) {
      return 0;
    }

    const position = this.audio.currentTime;

    if (!(position > 0)) {
      return 0;
    }

    // Clamped at 0:00 of THIS part — never into the previous one.
    const landing = Math.max(0, position - BREAK_RESUME_REWIND_SECONDS);

    // An anchor already held (an earlier rewind that hasn't settled yet, e.g. a cold
    // resume paused again within its settle window) survives this stacked rewind — using
    // `position` instead would replace the real stored offset with this resume's
    // pre-rewind position and let a second quick pause walk it backwards.
    this.holdPausedItemAnchor(this.pausedItemAnchor ?? position, landing);
    this.audio.currentTime = landing;
    this.update({ currentTime: landing });

    return position - landing;
  }

  private holdPausedItemAnchor(anchor: number, landing: number): void {
    this.pausedItemAnchor = anchor;
    this.anchorReleaseTime = landing + REWIND_SETTLE_SECONDS;
  }

  // The anchor lets go once the audio has actually played past the landing point — not
  // on the seek's own timeupdate (currentTime jumps there without a byte being heard).
  private releasePausedItemAnchorIfSettled(): void {
    if (
      this.pausedItemAnchor !== null &&
      this.resumeSeekTarget === null &&
      !this.audio.paused &&
      this.audio.currentTime >= this.anchorReleaseTime
    ) {
      this.pausedItemAnchor = null;
    }
  }

  // ── Idle listening (iOS PlaylistManager idle extension) ─────────────────────

  // Every player control the user touches counts as presence (iOS posts
  // PLAYER_USER_INTERACTION_DETECTED from seek/skip/lock-screen/in-app play).
  private markInteraction(): void {
    this.lastInteractionMs = Date.now();
  }

  private startIdleTracking(): void {
    if (this.sleepTimer !== null || this.idleTimeoutSeconds === null || this.idleTimer !== null) {
      return;
    }

    this.idleTimer = setInterval(() => this.checkIdleTimeout(), 1000);
  }

  private cancelIdleTracking(): void {
    if (this.idleTimer !== null) {
      clearInterval(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private checkIdleTimeout(): void {
    const timeout = this.idleTimeoutSeconds;

    if (timeout === null) {
      this.cancelIdleTracking();
      return;
    }

    const limitMs = timeout * 1000 * Math.pow(IDLE_TIMEOUT_GROWTH, this.idleFireCount);

    if (Date.now() - this.lastInteractionMs >= limitMs) {
      this.onIdleTimeoutFired();
    }
  }

  // iOS onIdleListeningTimerFired: pauseSilently (a real pause for pausedItem, but not a
  // "playback paused" analytics event) + the "Still there?" prompt, here an inline panel
  // notice. The fire count only ever grows within the session.
  private onIdleTimeoutFired(): void {
    this.idleFireCount += 1;
    this.cancelIdleTracking();

    if (!this.pausePlayback()) {
      return;
    }

    this.update({ idlePaused: true });
    this.analytics.track('idle_listening_paused', {
      timeout_seconds: this.idleTimeoutSeconds,
      fire_count: this.idleFireCount,
    });
  }

  // ── Media Session ───────────────────────────────────────────────────────────

  private syncMediaSession(): void {
    if (!('mediaSession' in navigator)) {
      return;
    }

    const item = this.snapshot.item;

    if (!item) {
      return;
    }

    navigator.mediaSession.metadata = new MediaMetadata({
      title: item.title ?? 'Peech',
      artist:
        this.snapshot.partsCount > 1
          ? `Part ${this.snapshot.partIndex + 1} of ${this.snapshot.partsCount}`
          : 'Peech',
      artwork: item.logoUrl ? [{ src: item.logoUrl }] : [],
    });

    navigator.mediaSession.setActionHandler('play', () => {
      // Headset/lock-screen Play must work from every resting state, like the panel's
      // button: replay after natural completion, retry after an error.
      if (this.snapshot.status === 'ended') {
        this.replayEndedItem();
      } else if (this.snapshot.status === 'error') {
        this.retryPart();
      } else {
        this.resume();
      }
    });
    navigator.mediaSession.setActionHandler('pause', () => this.pause());
    navigator.mediaSession.setActionHandler('seekbackward', () => this.skipBackward());
    navigator.mediaSession.setActionHandler('seekforward', () => this.skipForward());
    navigator.mediaSession.setActionHandler('seekto', details => {
      if (details.seekTime !== undefined) {
        this.seek(details.seekTime);
      }
    });
    navigator.mediaSession.setActionHandler(
      'previoustrack',
      this.snapshot.partsCount > 1 ? () => this.previousPart() : null
    );
    navigator.mediaSession.setActionHandler(
      'nexttrack',
      this.snapshot.partsCount > 1 ? () => this.nextPart() : null
    );
  }

  private clearMediaSession(): void {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = null;
    }
  }

  private update(patch: Partial<PlayerSnapshot>): void {
    this.snapshotSubject.next({ ...this.snapshotSubject.value, ...patch });
  }
}

// Resume at the furthest part; only a genuinely completed item restarts from the top.
// (iOS also restarts when furthest is the last part, but part-level progress can't tell
// "paused mid-final-part" from "finished" — completion state can, so trust it alone.)
function resumePartIndex(item: LibraryItem): number {
  const partsCount = Math.max(1, item.partsCount ?? 1);
  const furthest = Math.max(item.listeningPartIndex ?? 0, item.readingItemPartIndex ?? 0);

  if (item.libraryState === 'completed' || item.isFullyRead) {
    return 0;
  }

  return Math.min(furthest, partsCount - 1);
}

function clampPart(partIndex: number, partsCount: number): number {
  return Math.min(Math.max(0, partIndex), partsCount - 1);
}

function restoreSpeed(): number {
  const stored = Number(localStorage.getItem(SPEED_STORAGE_KEY));

  return Number.isFinite(stored) && stored >= MIN_SPEED && stored <= MAX_SPEED ? stored : 1;
}
